import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Camera, ExternalLink, Search } from 'lucide-react';
import { fetchChecksProcessed, fetchCheckVendors } from '../api/checks';
import type { Check, VendorOption } from '../api/checks';
import { detailText } from '../api/products';
import ExportButton from '../components/ExportButton';
import CheckDetailPanel from '../components/CheckDetailPanel';
import { HeaderActions } from '../components/HeaderActions';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { FilterSelect } from '../components/ui/filter-select';
import { MultiSelect } from '../components/ui/multi-select';
import { DataTable } from '../components/data-table/DataTable';
import {
  ACTIONS_COL_WIDTH,
  ExpandedFields,
  ExpandedPanel,
  ExpandedSection,
  ScreenshotLink,
  expandColumn,
} from '../components/data-table/columns';
import {
  checkStatusColumn,
  nsIdColumn,
  pctDiffColumn,
  priceDateColumn,
  purchaseCostColumn,
  reviewerColumn,
  skuColumn,
  specialPriceColumn,
  timestampColumn,
  vendorColumn,
  vendorCostColumn,
} from '../components/data-table/checkColumns';
import { RowActionMenu, RowActions, type RowMenuItem } from '../components/data-table/RowActions';
import {
  ColumnsMenu,
  FilterBar,
  FilterField,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { MAGENTO_BASE_URL, NS_BASE_URL } from '../config/external_links';
import { fmtDate, fmtVendor } from '../utils/format';
import { useItemDetail } from '../hooks/useItemDetail';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

const OUTCOME_OPTIONS = [
  { value: '',              label: 'All' },
  { value: 'approved',      label: 'Synced (accepted)', dot: 'bg-green-500' },
  { value: 'auto_approved', label: 'Auto-synced',       dot: 'bg-green-500' },
  { value: 'skipped',       label: 'Skipped',           dot: 'bg-amber-500' },
  { value: 'disabled',      label: 'Disabled',          dot: 'bg-zinc-400'  },
];

const TYPE_OPTIONS = [
  { value: '',        label: 'All' },
  { value: 'safe',    label: 'Safe',    dot: 'bg-green-500' },
  { value: 'unsafe',  label: 'Unsafe',  dot: 'bg-red-600'   },
  { value: 'skipped', label: 'Skipped', dot: 'bg-amber-500' },
];

/** What ultimately happened to the check. */
function OutcomeBadge({ reviewStatus }: { reviewStatus: string }) {
  if (reviewStatus === 'skipped')       return <Badge variant="warning" dot="bg-amber-500">Skipped</Badge>;
  if (reviewStatus === 'disabled')      return <Badge variant="neutral" dot="bg-zinc-400">Disabled</Badge>;
  if (reviewStatus === 'auto_approved') return <Badge variant="success" dot="bg-green-500">Auto-synced</Badge>;
  return <Badge variant="success" dot="bg-green-500">Synced</Badge>;
}

export default function PriceChecksProcessedPage() {
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  const [vendorCodes, setVendorCodes]     = useState<string[]>([]);
  const [reviewStatus, setReviewStatus] = useState<'' | 'approved' | 'auto_approved' | 'skipped' | 'disabled'>('');
  const [checkType, setCheckType]         = useState<'' | 'safe' | 'unsafe' | 'skipped'>('');
  const [priceDateFrom, setPriceDateFrom] = useState('');
  const [priceDateTo, setPriceDateTo]     = useState('');
  const [syncedFrom, setSyncedFrom]       = useState('');
  const [syncedTo, setSyncedTo]           = useState('');

  const [items, setItems]     = useState<Check[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const { detailFor, loadDetail } = useItemDetail();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded({});
    try {
      const data = await fetchChecksProcessed({
        vendor_codes:  vendorCodes.length ? vendorCodes : undefined,
        review_status: reviewStatus || undefined,
        type:          checkType || undefined,
        price_date_from: priceDateFrom || undefined,
        price_date_to:   priceDateTo || undefined,
        synced_from:   syncedFrom || undefined,
        synced_to:     syncedTo || undefined,
        page,
        per_page: perPage,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, vendorCodes, reviewStatus, checkType, priceDateFrom, priceDateTo, syncedFrom, syncedTo]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchCheckVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
  }, []);

  function resetFilters() {
    setVendorCodes([]);
    setReviewStatus('');
    setCheckType('');
    setPriceDateFrom('');
    setPriceDateTo('');
    setSyncedFrom('');
    setSyncedTo('');
    setPage(1);
  }

  const hasActiveFilters =
    vendorCodes.length > 0 || !!reviewStatus || !!checkType ||
    !!priceDateFrom || !!priceDateTo || !!syncedFrom || !!syncedTo;

  const columns = useMemo<ColumnDef<Check>[]>(() => [
    expandColumn<Check>(),
    skuColumn,
    vendorColumn,
    nsIdColumn,
    purchaseCostColumn,
    vendorCostColumn,
    specialPriceColumn,
    pctDiffColumn,
    { ...checkStatusColumn, meta: { label: 'Type' }, header: 'Type' },
    {
      id: 'outcome',
      meta: { label: 'Outcome' },
      header: 'Outcome',
      cell: ({ row }) => <OutcomeBadge reviewStatus={row.original.review_status} />,
    },
    reviewerColumn,
    priceDateColumn,
    timestampColumn('syncedAt', 'Synced at', c => c.ns_pushed_at),
    {
      id: 'actions',
      size: ACTIONS_COL_WIDTH,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right', padding: 'tight' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const menuItems: RowMenuItem[] = [
          {
            label: 'Details',
            icon: Search,
            href: `/all-items/${item.ns_item_id}?from=processed&checkId=${item.id}`,
          },
          { label: 'Open in NetSuite', icon: ExternalLink, href: `${NS_BASE_URL}${item.ns_item_id}` },
        ];
        if (item.magento_id != null) {
          menuItems.push({
            label: 'Open in Magento', icon: ExternalLink, href: `${MAGENTO_BASE_URL}${item.magento_id}`,
          });
        }
        if (item.screenshot_url) {
          menuItems.push({
            label: 'View screenshot', icon: Camera, href: item.screenshot_url, separatorBefore: true,
          });
        }
        return (
          <RowActions>
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    },
  ], []);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.id),
    state: { expanded, columnVisibility },
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: { columnPinning: { left: ['expand', 'sku'], right: ['actions'] } },
  });

  useEffect(() => {
    if (expanded === true) return;
    Object.keys(expanded)
      .filter(id => expanded[id])
      .forEach(id => {
        const item = items.find(i => String(i.id) === id);
        if (item) void loadDetail(item.ns_item_id);
      });
  }, [expanded, items, loadDetail]);

  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  return (
    <>
      <HeaderActions>
        <ExportButton
          resource="checks_processed"
          filters={{
            vendor_codes: vendorCodes.length ? vendorCodes : undefined,
            review_status: reviewStatus || undefined,
            type: checkType || undefined,
            price_date_from: priceDateFrom || undefined,
            price_date_to: priceDateTo || undefined,
            synced_from: syncedFrom || undefined,
            synced_to: syncedTo || undefined,
          }}
        />
      </HeaderActions>

      <PageHeading title="Price Checks — Processed" count={total} />

      <FilterBar>
        <FilterRow>
          <MultiSelect
            label="Vendor"
            options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
            selected={vendorCodes}
            onChange={next => { setVendorCodes(next); setPage(1); }}
          />
          <FilterSelect
            label="Outcome"
            options={OUTCOME_OPTIONS}
            value={reviewStatus}
            onChange={v => { setReviewStatus(v as typeof reviewStatus); setPage(1); }}
            contentClassName="w-[220px]"
          />
          <FilterSelect
            label="Type"
            options={TYPE_OPTIONS}
            value={checkType}
            onChange={v => { setCheckType(v as typeof checkType); setPage(1); }}
          />
        </FilterRow>

        <FilterRow className="gap-4">
          <FilterField label="Price date">
            <Input
              type="date"
              className="w-[140px]"
              value={priceDateFrom}
              onChange={e => { setPriceDateFrom(e.target.value); setPage(1); }}
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="date"
              className="w-[140px]"
              value={priceDateTo}
              onChange={e => { setPriceDateTo(e.target.value); setPage(1); }}
            />
          </FilterField>

          <FilterField label="Synced">
            <Input
              type="date"
              className="w-[140px]"
              value={syncedFrom}
              onChange={e => { setSyncedFrom(e.target.value); setPage(1); }}
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="date"
              className="w-[140px]"
              value={syncedTo}
              onChange={e => { setSyncedTo(e.target.value); setPage(1); }}
            />
          </FilterField>

          <div className="ml-auto flex items-center gap-2">
            {hasActiveFilters && <ResetFiltersButton onClick={resetFilters} />}
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="No processed checks found"
        renderExpanded={row => {
          const detail = detailFor(row.original.ns_item_id);
          return (
            <ExpandedPanel>
              <ExpandedSection title="Item">
                <ExpandedFields
                  rows={[
                    [
                      'Description',
                      detailText(detail.data, 'display_name') ?? detailText(detail.data, 'name') ?? '—',
                    ],
                    ['Manufacturer', detailText(detail.data, 'manufacturer') ?? '—'],
                    ['Type', detailText(detail.data, 'product_type') ?? '—'],
                    ['Vendor part no.', <span className="font-mono">{row.original.sku ?? '—'}</span>],
                    ['Last checked', fmtDate(row.original.price_date ?? row.original.created_at)],
                  ]}
                />
              </ExpandedSection>

              <ExpandedSection title="Price history">
                <CheckDetailPanel state={detail} currentId={row.original.id} />
              </ExpandedSection>

              <ExpandedSection title="Vendor screenshot">
                <ScreenshotLink url={row.original.screenshot_url} />
              </ExpandedSection>
            </ExpandedPanel>
          );
        }}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        onPageChange={setPage}
        onPerPageChange={n => { setPerPage(n); setPage(1); }}
      />
    </>
  );
}
